// MarketsTab.js — Market-level configuration tab for the Integrated Simulator.
// Users select a market, configure its YDM curve + params, and see backtested results.

import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceDot, ReferenceLine, ReferenceArea,
} from 'recharts';
import { useSession, useSessionDispatch, DEFAULT_MARKET } from './SessionContext';
import { computeYieldAtUtilization, backtestMarket, analyzeDrawdowns } from '../../lib/market-engine';
import { getNavData, toTimeseries } from '../../lib/nav-data-loader';
import COLORS from '../../lib/colors';

const fmtPct = (v, d = 2) => (v * 100).toFixed(d) + '%';
const toMs = (d) => new Date(d).getTime();
const fmtDateTick = (ms) => new Date(ms).toISOString().slice(5, 10);
/** Generate evenly spaced tick timestamps across a date range. */
const makeTimeTicks = (data) => {
  if (!data || data.length < 2) return [];
  const minMs = data[0].ts;
  const maxMs = data[data.length - 1].ts;
  const spanDays = (maxMs - minMs) / 86400000;
  const interval = spanDays <= 30 ? 3 : spanDays <= 90 ? 7 : 14;
  const ticks = [];
  for (let t = minMs; t <= maxMs; t += interval * 86400000) ticks.push(t);
  if (ticks[ticks.length - 1] < maxMs) ticks.push(maxMs);
  return ticks;
};
const tickStyle = { fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" };
const chartMargin = { top: 10, right: 30, left: 10, bottom: 10 };

// ── Market presets ──
// Live markets (existing on-chain, V1/V2 YDM curves with real data)
// Prospective markets (V2 defaults, sourced from DSV engine DEFAULT_STRATEGIES)

const MARKET_PRESETS = {
  // ── Prospective Dawn markets (with JT coverage) ──
  'Fasanara mf-ONE': { ydm: { version: 'V2', y0: 0.18, yT: 0.18, y100: 0.48, speed: 50 }, params: { minCoverage: 0.15, recoveryDuration: 7, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'Fasanara mf-ONE', yield: 12, utilization: 90 }, liquidation: { finalizedLossThreshold: 0.02, selfLiquidationBonus: 0.01 } },
  'Fasanara GDADF': { ydm: { version: 'V2', y0: 0.12, yT: 0.12, y100: 0.32, speed: 50 }, params: { minCoverage: 0.10, recoveryDuration: 7, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'Fasanara GDADF', yield: 9, utilization: 90 }, liquidation: { finalizedLossThreshold: 0.02, selfLiquidationBonus: 0.01 } },
  'Apollo ACRED (Securitize)': { ydm: { version: 'V2', y0: 0.22, yT: 0.22, y100: 0.59, speed: 50 }, params: { minCoverage: 0.18, recoveryDuration: 7, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'Apollo ACRED (Securitize)', yield: 9, utilization: 90 }, liquidation: { finalizedLossThreshold: 0.02, selfLiquidationBonus: 0.01 } },
  'Pareto FalconX': { ydm: { version: 'V2', y0: 0.15, yT: 0.15, y100: 0.37, speed: 50 }, params: { minCoverage: 0.12, recoveryDuration: 7, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'Pareto FalconX', yield: 9.38, utilization: 90 }, liquidation: { finalizedLossThreshold: 0.02, selfLiquidationBonus: 0.01 } },
  'Maple Institutional': { ydm: { version: 'V2', y0: 0.077, yT: 0.077, y100: 0.40, speed: 80 }, params: { minCoverage: 0.10, recoveryDuration: 7, stFee: 0.10, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'apy', dataKey: 'Maple Institutional', yield: 5.9, utilization: 90 }, liquidation: { finalizedLossThreshold: 0.05, selfLiquidationBonus: 0.01 } },
  'Maple Syrup (public)': { ydm: { version: 'V2', y0: 0.03, yT: 0.03, y100: 0.1, speed: 75 }, params: { minCoverage: 0.03, recoveryDuration: 0, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'apy', dataKey: 'Maple Syrup (public)', yield: 4.5, utilization: 90 }, liquidation: { finalizedLossThreshold: 0.005, selfLiquidationBonus: 0 } },
  'Smokehouse USDC v1': { ydm: { version: 'V2', y0: 0.07, yT: 0.07, y100: 0.21, speed: 75 }, params: { minCoverage: 0.07, recoveryDuration: 0, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'Smokehouse USDC v1', yield: 4.31, utilization: 90 }, liquidation: { finalizedLossThreshold: 0, selfLiquidationBonus: 0 } },
  'Re Protocol reUSD': { ydm: { version: 'V2', y0: 0.09, yT: 0.09, y100: 0.49, speed: 80 }, params: { minCoverage: 0.10, recoveryDuration: 0, stFee: 0.10, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'Re Protocol reUSD', yield: 6.1, utilization: 90 }, liquidation: { finalizedLossThreshold: 0, selfLiquidationBonus: 0 } },
  'sUSDai': { ydm: { version: 'V2', y0: 0.11, yT: 0.11, y100: 0.31, speed: 50 }, params: { minCoverage: 0.1, recoveryDuration: 7, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'sUSDai', yield: 6.5, utilization: 90 }, liquidation: { finalizedLossThreshold: 0.01, selfLiquidationBonus: 0 } },
  'Neutrl sNUSD': { ydm: { version: 'V2', y0: 0.11, yT: 0.11, y100: 0.31, speed: 50 }, params: { minCoverage: 0.1, recoveryDuration: 0, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'Neutrl sNUSD', yield: 9.5, utilization: 90 }, liquidation: { finalizedLossThreshold: 0, selfLiquidationBonus: 0 } },
  'Avant savUSD': { ydm: { version: 'V2', y0: 0.1, yT: 0.1, y100: 0.31, speed: 50 }, params: { minCoverage: 0.1, recoveryDuration: 0, stFee: 0.1, jtFee: 0, ysFee: 0.45 }, underlying: { source: 'nav', dataKey: 'Avant savUSD', yield: 7.4, utilization: 90 }, liquidation: { finalizedLossThreshold: 0, selfLiquidationBonus: 0 } },
  'Strategy Inc STRC': { ydm: { version: 'V2', y0: 0.30, yT: 0.30, y100: 0.70, speed: 50 }, params: { minCoverage: 0.25, recoveryDuration: 30, stFee: 0.1, jtFee: 0, ysFee: 0.30 }, underlying: { source: 'nav', dataKey: 'Strategy Inc STRC', yield: 11, utilization: 90 }, liquidation: { finalizedLossThreshold: 0.125, selfLiquidationBonus: 0.02 } },
  // ── Prospective Reserve markets (no JT) ──
  'Reserve Sky sUSDS': { ydm: { version: 'V2', y0: 0, yT: 0, y100: 0, speed: 0 }, params: { minCoverage: 0, recoveryDuration: 0, stFee: 0, jtFee: 0, ysFee: 0 }, underlying: { source: 'nav', dataKey: 'Reserve Sky sUSDS', yield: 4, utilization: 0 }, liquidation: { finalizedLossThreshold: 0, selfLiquidationBonus: 0 } },
};

// ── Slider thumb styling (pseudo-elements can't be set via inline styles) ──
const sliderThumbStyle = document.createElement('style');
sliderThumbStyle.textContent = `
  .integrated-slider::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 14px; height: 14px; border-radius: 50%;
    background: ${COLORS.accent}; border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2); cursor: pointer; margin-top: 0px;
  }
  .integrated-slider::-moz-range-thumb {
    width: 14px; height: 14px; border-radius: 50%;
    background: ${COLORS.accent}; border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2); cursor: pointer;
  }
`;
if (!document.querySelector('[data-integrated-slider-style]')) {
  sliderThumbStyle.setAttribute('data-integrated-slider-style', '');
  document.head.appendChild(sliderThumbStyle);
}

// ── Inline UI components ──

const Slider = ({ label, value, onChange, min, max, step, suffix = '' }) => {
  const decimals = step < 0.01 ? 3 : step < 0.1 ? 2 : step < 1 ? 1 : 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const startEdit = () => { setDraft(typeof value === 'number' ? value.toFixed(decimals) : String(value)); setEditing(true); };
  const commitEdit = () => {
    setEditing(false);
    const v = parseFloat(draft);
    if (!isNaN(v)) onChange(Math.min(Math.max(v, min), max));
  };
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>{label}</span>
        {editing ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
            <input type="text" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
              onBlur={commitEdit} onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(false); }}
              style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: COLORS.ink, background: COLORS.bg, border: `1px solid ${COLORS.accent}`, borderRadius: 2, padding: '1px 6px', width: 64, textAlign: 'right', outline: 'none' }} />
            <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: COLORS.ink }}>{suffix}</span>
          </div>
        ) : (
          <span onClick={startEdit} style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: COLORS.ink, cursor: 'text', borderBottom: `1px dashed ${COLORS.champagne}80`, paddingBottom: 1 }}>
            {typeof value === 'number' ? value.toFixed(decimals) : value}{suffix}
          </span>
        )}
      </div>
      <input type="range" className="integrated-slider" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', height: 4, appearance: 'none', WebkitAppearance: 'none', background: `linear-gradient(to right, ${COLORS.accent} 0%, ${COLORS.accent} ${((value - min) / (max - min)) * 100}%, ${COLORS.borderDark} ${((value - min) / (max - min)) * 100}%, ${COLORS.borderDark} 100%)`, borderRadius: 2, outline: 'none', cursor: 'pointer' }} />
    </div>
  );
};

const Card = ({ title, children }) => (
  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4, padding: 16, marginBottom: 12 }}>
    {title && <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: 12 }}>{title}</div>}
    {children}
  </div>
);

const MetricRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>{label}</span>
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: color || COLORS.ink }}>{value}</span>
  </div>
);

const ChartTooltip = ({ active, payload, labelFormatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: COLORS.ink, color: COLORS.bg, padding: '8px 12px', borderRadius: 2, fontFamily: "'Fragment Mono', monospace", fontSize: 11, lineHeight: 1.6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
      {labelFormatter && <div style={{ marginBottom: 2 }}>{labelFormatter(payload[0]?.payload)}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color === COLORS.ink ? COLORS.bg : p.color }}>{p.name}: {typeof p.value === 'number' ? (p.value * 100).toFixed(2) + '%' : p.value}</div>
      ))}
    </div>
  );
};

const thStyle = { textAlign: 'left', padding: '6px 8px', color: COLORS.textSecondary, fontWeight: 400, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: 10 };
const tdStyle = { padding: '8px 8px', color: COLORS.ink };

const episodeColors = (ep) => ({
  color: ep.recovered ? COLORS.positive : ep.jtLossFinalized ? COLORS.negative : COLORS.caution,
  border: ep.recovered ? COLORS.positiveBorder : ep.jtLossFinalized ? COLORS.negativeBorder : COLORS.cautionBorder,
});

const EpisodeDetail = ({ episode, onClose }) => {
  const { color, border } = episodeColors(episode);
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: COLORS.surfaceAlt, border: `1px solid ${border}`, borderRadius: 4, fontFamily: "'Fragment Mono', monospace", fontSize: 11, position: 'relative' }}>
      <button onClick={onClose} style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', cursor: 'pointer', color: COLORS.textTertiary, fontSize: 13, lineHeight: 1 }}>✕</button>
      <div style={{ color, fontWeight: 600, marginBottom: 6 }}>
        {episode.recovered ? 'Recovered' : episode.jtLossFinalized ? 'JT Loss Finalized' : 'Ongoing'}
        {episode.jtLossCause && <span style={{ fontWeight: 400, color: COLORS.textSecondary, marginLeft: 8 }}>— {episode.jtLossCause}</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '3px 20px', color: COLORS.textSecondary }}>
        <span>Period</span><span style={{ color: COLORS.text }}>{episode.startDate} → {episode.endDate} ({episode.durationDays}d)</span>
        <span>Trough</span><span style={{ color: COLORS.negative }}>{episode.troughDate} ({fmtPct(episode.drawdownPct, 3)})</span>
        {!episode.recovered && episode.endDrawdownPct != null && (
          <><span>DD at close</span><span style={{ color: COLORS.negative }}>{fmtPct(episode.endDrawdownPct, 3)}</span></>
        )}
        {episode.jtLossFinalized && (
          <><span>JT Loss</span><span style={{ color: COLORS.negative, fontWeight: 600 }}>{episode.jtLossPct <= -1 ? '−100%' : fmtPct(episode.jtLossPct)}</span></>
        )}
      </div>
    </div>
  );
};

// ── Main component ──

export default function MarketsTab() {
  const { state } = useSession();
  const dispatch = useSessionDispatch();
  const [selectedMarket, setSelectedMarket] = useState('');
  const [showCoverageLine, setShowCoverageLine] = useState(true);
  const [showLiquidationLine, setShowLiquidationLine] = useState(true);

  // Seed presets on first render if no markets exist
  useEffect(() => {
    if (Object.keys(state.markets).length === 0) {
      for (const [name, config] of Object.entries(MARKET_PRESETS)) {
        dispatch({ type: 'ADD_MARKET', name, config });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first market when markets are populated
  useEffect(() => {
    const names = Object.keys(state.markets);
    if (names.length > 0 && !state.markets[selectedMarket]) {
      setSelectedMarket(names[0]);
    }
  }, [state.markets, selectedMarket]);

  const market = state.markets[selectedMarket] || DEFAULT_MARKET;

  // ── Date range filter ──
  const [dateRangeMode, setDateRangeMode] = useState('custom90'); // 'latest90' | 'custom90' | 'custom' | 'full'
  const [customStartDate, setCustomStartDate] = useState('2025-11-28');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  // Clear episode selection when any simulation input changes (market, date range, params, ydm, liquidation)
  useEffect(() => { setSelectedEpisode(null); }, [selectedMarket, dateRangeMode, customStartDate, customEndDate, market.ydm, market.params, market.liquidation]);

  // Dispatch helper — patches the selected market
  const updateMarket = useCallback((patch) => {
    if (!selectedMarket) return;
    dispatch({ type: 'UPDATE_MARKET', name: selectedMarket, patch });
  }, [dispatch, selectedMarket]);

  // ── YDM curve data (200 points) ──
  const curveData = useMemo(() => {
    const points = [];
    for (let i = 0; i <= 200; i++) {
      const u = i / 200;
      const y = computeYieldAtUtilization(u, market.ydm);
      points.push({ u, y });
    }
    return points;
  }, [market.ydm]);

  // Current utilization point on curve
  const currentU = market.underlying.utilization / 100;
  const currentY = useMemo(() => computeYieldAtUtilization(currentU, market.ydm), [currentU, market.ydm]);

  // ── Backtest data ──
  const navData = useMemo(() => {
    if (!market.underlying.dataKey) return null;
    return getNavData(market.underlying.dataKey);
  }, [market.underlying.dataKey]);

  // Filtered nav data based on date range mode
  const filteredNavData = useMemo(() => {
    if (!navData) return null;
    const { dates, navs } = navData;
    if (dateRangeMode === 'full') return navData;
    if (dateRangeMode === 'latest90') {
      const lastMs = new Date(dates[dates.length - 1]).getTime();
      const cutoffMs = lastMs - 90 * 24 * 60 * 60 * 1000;
      const start = dates.findIndex(d => new Date(d).getTime() >= cutoffMs);
      const startIdx = start === -1 ? 0 : start;
      return { dates: dates.slice(startIdx), navs: navs.slice(startIdx) };
    }
    // custom90: 90 calendar days from customStartDate
    if (dateRangeMode === 'custom90' && customStartDate) {
      const startMs = new Date(customStartDate).getTime();
      const endMs = startMs + 90 * 24 * 60 * 60 * 1000;
      const startIdx = dates.findIndex(d => new Date(d).getTime() >= startMs);
      if (startIdx === -1) return navData;
      const endIdx = dates.findIndex(d => new Date(d).getTime() > endMs);
      const slice = endIdx === -1 ? dates.length : endIdx;
      return { dates: dates.slice(startIdx, slice), navs: navs.slice(startIdx, slice) };
    }
    // custom: arbitrary start + end dates
    if (dateRangeMode === 'custom' && customStartDate) {
      const startMs = new Date(customStartDate).getTime();
      const startIdx = dates.findIndex(d => new Date(d).getTime() >= startMs);
      if (startIdx === -1) return navData;
      let endSlice = dates.length;
      if (customEndDate) {
        const endMs = new Date(customEndDate).getTime();
        const endIdx = dates.findIndex(d => new Date(d).getTime() > endMs);
        if (endIdx !== -1) endSlice = endIdx;
      }
      return { dates: dates.slice(startIdx, endSlice), navs: navs.slice(startIdx, endSlice) };
    }
    return navData;
  }, [navData, dateRangeMode, customStartDate, customEndDate]);

  const backtestResult = useMemo(() => {
    if (!filteredNavData) return null;
    const navTimeseries = toTimeseries(filteredNavData);
    if (navTimeseries.length < 2) return null;
    return backtestMarket({
      navTimeseries,
      ydm: market.ydm,
      params: market.params,
      liquidation: market.liquidation,
      utilization: currentU,
    });
  }, [filteredNavData, market.ydm, market.params, market.liquidation, currentU]);

  // ── Drawdown-at-start detection ──
  // Check if the backtest start date falls inside an ongoing drawdown in the full dataset.
  const startInDrawdown = useMemo(() => {
    if (!navData || !filteredNavData || !filteredNavData.dates.length) return null;
    const startDate = filteredNavData.dates[0];
    const { dates, navs } = navData;
    let peak = navs[0];
    for (let i = 0; i < dates.length; i++) {
      if (navs[i] > peak) peak = navs[i];
      if (dates[i] === startDate) {
        if (navs[i] < peak) {
          const dd = (navs[i] - peak) / peak;
          return { drawdownPct: dd, peakDate: dates[[...navs].slice(0, i + 1).indexOf(peak)] };
        }
        return null;
      }
    }
    return null;
  }, [navData, filteredNavData]);

  // ── Chart data arrays ──
  const cumReturnData = useMemo(() => {
    if (!backtestResult || !filteredNavData) return [];
    const baseNav = filteredNavData.navs[0] || 1;
    return backtestResult.dates.map((date, i) => ({
      date,
      ts: toMs(date),
      st: backtestResult.st.cumReturn[i],
      jt: backtestResult.jt.cumReturn[i],
      underlying: filteredNavData.navs[i] != null ? filteredNavData.navs[i] / baseNav - 1 : 0,
    }));
  }, [backtestResult, filteredNavData]);

  const trailingApyData = useMemo(() => {
    if (!backtestResult) return [];
    const WINDOW = 7;
    // Compute underlying 7-day trailing APY from filteredNavData, aligned to backtestResult.dates
    const underlyingByDate = {};
    if (filteredNavData && filteredNavData.navs && filteredNavData.dates) {
      const { navs, dates } = filteredNavData;
      for (let i = 1; i < dates.length; i++) {
        // Find the entry closest to WINDOW calendar days back
        const currentMs = new Date(dates[i]).getTime();
        const cutoffMs = currentMs - WINDOW * 86400000;
        let j = i - 1;
        while (j > 0 && new Date(dates[j]).getTime() > cutoffMs) j--;
        const startMs = new Date(dates[j]).getTime();
        const calendarDays = Math.max((currentMs - startMs) / 86400000, 1);
        const windowReturn = navs[i] / navs[j];
        underlyingByDate[dates[i]] = Math.pow(windowReturn, 365 / calendarDays) - 1;
      }
    }
    return backtestResult.dates.map((date, i) => ({
      date,
      ts: toMs(date),
      st: backtestResult.st.trailingApy[i],
      jt: backtestResult.jt.trailingApy[i],
      underlying: underlyingByDate[date] ?? null,
    }));
  }, [backtestResult, filteredNavData]);

  const dailyReturnData = useMemo(() => {
    if (!backtestResult) return [];
    // Compute underlying daily return from raw NAV
    const underlyingByDate = {};
    if (filteredNavData && filteredNavData.navs && filteredNavData.dates) {
      const { navs, dates } = filteredNavData;
      for (let i = 1; i < dates.length; i++) {
        underlyingByDate[dates[i]] = navs[i] / navs[i - 1] - 1;
      }
    }
    return backtestResult.dates.map((date, i) => ({
      date,
      ts: toMs(date),
      st: backtestResult.st.dailyReturn[i],
      jt: backtestResult.jt.dailyReturn[i],
      underlying: underlyingByDate[date] ?? null,
    }));
  }, [backtestResult, filteredNavData]);

  // ── Drawdown analysis data ──
  const { drawdownChartData, drawdownEpisodes, jtLossEvents, coverageExhaustionLevel, liquidationThreshold } = useMemo(() => {
    if (!filteredNavData || !filteredNavData.navs || filteredNavData.navs.length < 2) {
      return { drawdownChartData: [], drawdownEpisodes: [], jtLossEvents: [], coverageExhaustionLevel: 0, liquidationThreshold: 0 };
    }

    // Protocol-aware drawdown analysis via engine
    // drawdownSeries uses same peak-reset logic as episode detection
    const navTimeseries = toTimeseries(filteredNavData);
    const analysis = analyzeDrawdowns({
      navTimeseries,
      params: market.params,
      liquidation: market.liquidation,
      utilization: currentU,
    });

    // Coverage exhaustion level — uses actual utilization
    const minCov = market.params.minCoverage;
    const covLevel = minCov > 0 ? -(minCov / currentU) : -1;

    // Liquidation threshold — FLT is enforced from min coverage, so we add
    // the buffer between current coverage and min coverage
    const rawFlt = market.liquidation.finalizedLossThreshold || 0;
    const coverageBuffer = minCov > 0 ? (minCov / currentU - minCov) / (1 - minCov) : 0;
    const flt = rawFlt > 0 ? -(1 - (1 - coverageBuffer) * (1 - rawFlt)) : 0;

    return {
      drawdownChartData: analysis.drawdownSeries.map(d => ({ ...d, ts: toMs(d.date) })),
      drawdownEpisodes: analysis.episodes,
      jtLossEvents: analysis.jtLossEvents,
      coverageExhaustionLevel: covLevel,
      liquidationThreshold: flt,
    };
  }, [filteredNavData, market.params, market.liquidation, currentU]);

  const summary = backtestResult?.summary;

  // ── Render ──
  return (
    <div style={{ padding: '20px 24px' }}>
      {/* Market selector + date range */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select
          value={selectedMarket}
          onChange={(e) => setSelectedMarket(e.target.value)}
          style={{
            fontFamily: "'Fragment Mono', monospace", fontSize: 13, padding: '8px 12px',
            background: COLORS.surface, color: COLORS.ink, border: `1px solid ${COLORS.borderLight}`,
            borderRadius: 4, cursor: 'pointer', minWidth: 200, outline: 'none',
          }}
        >
          {Object.keys(state.markets).map(name => (
            <option key={name} value={name}>
              {state.markets[name].status === 'complete' ? '\u25CF' : '\u25CB'} {name}
            </option>
          ))}
        </select>

        {/* Date range selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px', background: COLORS.surface, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4 }}>
          {[
            { key: 'full', label: 'Full dataset' },
            { key: 'latest90', label: 'Latest 90d' },
            { key: 'custom90', label: '90d from' },
            { key: 'custom', label: 'Custom' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setDateRangeMode(key)} style={{
              fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '4px 10px',
              background: dateRangeMode === key ? COLORS.ink : 'transparent',
              color: dateRangeMode === key ? COLORS.bg : COLORS.textSecondary,
              border: 'none', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              {label}
            </button>
          ))}
          {(dateRangeMode === 'custom90' || dateRangeMode === 'custom') && (
            <input
              type="date"
              value={customStartDate}
              min={navData?.dates?.[0] ?? ''}
              max={navData?.dates?.[navData.dates.length - 1] ?? ''}
              onChange={(e) => setCustomStartDate(e.target.value)}
              style={{
                fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '3px 6px',
                background: COLORS.bg, color: COLORS.ink, border: `1px solid ${COLORS.borderLight}`,
                borderRadius: 3, outline: 'none', marginLeft: 2,
              }}
            />
          )}
          {dateRangeMode === 'custom' && (
            <>
              <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textTertiary }}>→</span>
              <input
                type="date"
                value={customEndDate}
                min={customStartDate || navData?.dates?.[0] || ''}
                max={navData?.dates?.[navData.dates.length - 1] ?? ''}
                onChange={(e) => setCustomEndDate(e.target.value)}
                style={{
                  fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '3px 6px',
                  background: COLORS.bg, color: COLORS.ink, border: `1px solid ${COLORS.borderLight}`,
                  borderRadius: 3, outline: 'none',
                }}
              />
            </>
          )}
        </div>

        <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textTertiary }}>
          {Object.keys(state.markets).filter(n => state.markets[n].status === 'complete').length} / {Object.keys(state.markets).length} complete
        </span>
      </div>
      {selectedMarket === 'Strategy Inc STRC' && (
        <div style={{
          fontFamily: "'Fragment Mono', monospace", fontSize: 10, color: COLORS.positive,
          background: COLORS.positiveBg, border: `1px solid ${COLORS.positiveBorder}`,
          borderRadius: 4, padding: '5px 10px', marginBottom: 8,
        }}>
          NAV methodology: total return index with dividends reinvested at ex-date close price. nav_price = share_multiplier × close / 100 par.
        </div>
      )}
      {startInDrawdown && (
        <div style={{
          fontFamily: "'Fragment Mono', monospace", fontSize: 10, color: COLORS.caution,
          background: COLORS.cautionBg, border: `1px solid ${COLORS.cautionBorder}`,
          borderRadius: 4, padding: '5px 10px', marginBottom: 12,
        }}>
          Start date is inside an ongoing drawdown ({fmtPct(startInDrawdown.drawdownPct, 2)} from peak on {startInDrawdown.peakDate}). Backtest begins mid-episode — Fixed-Term behavior may activate immediately.
        </div>
      )}

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, alignItems: 'start' }}>
        {/* Left panel — config */}
        <div>
          <Card title="YDM Curve Config">
            <Slider label="Y₀" value={market.ydm.y0 * 100} onChange={(v) => updateMarket({ ydm: { y0: v / 100 } })} min={0} max={100} step={0.5} suffix="%" />
            <Slider label="Y_T" value={market.ydm.yT * 100} onChange={(v) => updateMarket({ ydm: { yT: v / 100 } })} min={0} max={100} step={0.5} suffix="%" />
            <Slider label="Y₁₀₀" value={market.ydm.y100 * 100} onChange={(v) => updateMarket({ ydm: { y100: v / 100 } })} min={0} max={100} step={1} suffix="%" />
            {(market.ydm.y0 > market.ydm.yT || market.ydm.yT > market.ydm.y100) && (
              <div style={{ padding: '6px 10px', marginBottom: 10, borderRadius: 3, background: COLORS.negativeBg, border: `1px solid ${COLORS.negativeBorder}`, fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.negative }}>
                Constraint violated: Y₀ ≤ Y_T ≤ Y₁₀₀
              </div>
            )}
            <Slider label="Speed" value={market.ydm.speed} onChange={(v) => updateMarket({ ydm: { speed: v } })} min={0} max={500} step={10} suffix=" ×/yr" />
          </Card>

          <Card title="Market Params">
            <Slider label="Min Coverage" value={market.params.minCoverage * 100} onChange={(v) => updateMarket({ params: { minCoverage: v / 100 } })} min={0} max={50} step={1} suffix="%" />
            <Slider label="Recovery Duration" value={market.params.recoveryDuration} onChange={(v) => updateMarket({ params: { recoveryDuration: v } })} min={0} max={90} step={1} suffix=" days" />
            <Slider label="ST Fee" value={market.params.stFee * 100} onChange={(v) => updateMarket({ params: { stFee: v / 100 } })} min={0} max={50} step={1} suffix="%" />
            <Slider label="JT Fee" value={market.params.jtFee * 100} onChange={(v) => updateMarket({ params: { jtFee: v / 100 } })} min={0} max={50} step={1} suffix="%" />
            <Slider label="Yield Share Fee" value={market.params.ysFee * 100} onChange={(v) => updateMarket({ params: { ysFee: v / 100 } })} min={0} max={100} step={1} suffix="%" />
          </Card>

          <Card title="Liquidation">
            <Slider label="Finalized Loss Threshold" value={market.liquidation.finalizedLossThreshold * 100} onChange={(v) => updateMarket({ liquidation: { finalizedLossThreshold: v / 100 } })} min={0} max={20} step={0.5} suffix="%" />
            <Slider label="Self-Liquidation Bonus" value={market.liquidation.selfLiquidationBonus * 100} onChange={(v) => updateMarket({ liquidation: { selfLiquidationBonus: v / 100 } })} min={0} max={10} step={0.5} suffix="%" />
          </Card>

          <Card title="Underlying">
            <div style={{ marginBottom: 12 }}>
              <span style={{
                display: 'inline-block', fontFamily: "'Fragment Mono', monospace", fontSize: 10,
                letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 2,
                background: market.underlying.source === 'nav' ? COLORS.positiveBg : COLORS.cautionBg,
                color: market.underlying.source === 'nav' ? COLORS.positive : COLORS.caution,
                border: `1px solid ${market.underlying.source === 'nav' ? COLORS.positiveBorder : COLORS.cautionBorder}`,
              }}>
                {market.underlying.source === 'nav' ? 'NAV-derived' : 'APY-derived'}
              </span>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: 4 }}>Data Source</div>
              <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '6px 8px', background: COLORS.surfaceAlt, color: market.underlying.dataKey ? COLORS.ink : COLORS.textTertiary, border: `1px solid ${COLORS.borderLight}`, borderRadius: 2 }}>
                {market.underlying.dataKey || '— None —'}
              </div>
            </div>
            <Slider label="Underlying Yield" value={market.underlying.yield} onChange={(v) => updateMarket({ underlying: { yield: v } })} min={0} max={30} step={0.1} suffix="%" />
            <Slider label="Utilization" value={market.underlying.utilization} onChange={(v) => updateMarket({ underlying: { utilization: v } })} min={0} max={100} step={0.5} suffix="%" />
          </Card>

          {/* Completion toggle */}
          <button
            onClick={() => {
              const newStatus = market.status === 'complete' ? 'incomplete' : 'complete';
              dispatch({ type: 'SET_MARKET_STATUS', name: selectedMarket, status: newStatus });
              // On localhost, save updated preset back to source when marking complete
              const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              if (isLocal && newStatus === 'complete') {
                const { status, ...preset } = market;
                fetch('/api/save-market-preset', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ name: selectedMarket, config: { ...preset } }),
                }).then(r => r.json()).then(r => {
                  if (!r.ok) console.warn('save-market-preset:', r.error);
                }).catch(err => console.warn('save-market-preset:', err));
              }
            }}
            style={{
              width: '100%', padding: '10px 0', fontFamily: "'Fragment Mono', monospace", fontSize: 12,
              letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: 4,
              background: market.status === 'complete' ? COLORS.positiveBg : 'transparent',
              color: market.status === 'complete' ? COLORS.positive : COLORS.textSecondary,
              border: `1px solid ${market.status === 'complete' ? COLORS.positiveBorder : COLORS.borderLight}`,
            }}
          >
            {market.status === 'complete' ? 'Mark Incomplete' : 'Mark Complete'}
          </button>
        </div>

        {/* Right panel — charts */}
        <div>
          {/* Summary + YDM Curve row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {summary ? (
              <Card title="Backtest Summary">
                <MetricRow label="Underlying Annualized" value={fmtPct(filteredNavData && filteredNavData.navs.length > 1 && summary.days > 0 ? Math.pow(filteredNavData.navs[filteredNavData.navs.length - 1] / filteredNavData.navs[0], 365 / summary.days) - 1 : 0)} color={COLORS.textTertiary} />
                <MetricRow label="ST Annualized" value={fmtPct(summary.stAnnualized)} color={COLORS.accent} />
                <MetricRow label="JT Annualized" value={fmtPct(summary.jtAnnualized)} color={COLORS.positive} />
                <MetricRow label="Max Drawdown (ST)" value={fmtPct(summary.maxDrawdown)} color={COLORS.negative} />
                <MetricRow label="Loss Events" value={jtLossEvents.length} />
                <MetricRow label="Backtest Period" value={`${summary.days} days`} />

                {/* JT Loss Events */}
                {drawdownEpisodes.length > 0 && (
                  <div style={{ marginTop: 12, borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, fontFamily: "'Fragment Mono', monospace", color: COLORS.textSecondary, marginBottom: 6 }}>JT Loss Events</div>
                    {(() => {
                      const ongoingCount = drawdownEpisodes.filter(e => !e.recovered && !e.jtLossFinalized).length;
                      return jtLossEvents.length === 0 ? (
                        <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.positive }}>
                          No JT losses finalized during backtest period.
                          {ongoingCount > 0 && (
                            <span style={{ color: COLORS.caution, display: 'block', marginTop: 4 }}>
                              {ongoingCount} episode{ongoingCount !== 1 ? 's' : ''} still within recovery window.
                            </span>
                          )}
                        </div>
                      ) : (
                      <div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Fragment Mono', monospace", fontSize: 10 }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
                              <th style={thStyle}>Episode</th>
                              <th style={thStyle}>Trough</th>
                              <th style={thStyle}>DD</th>
                              <th style={thStyle}>JT Loss</th>
                              <th style={thStyle}>Cause</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jtLossEvents.map((ev, idx) => (
                              <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.borderLight}08` }}>
                                <td style={tdStyle}>{ev.startDate} → {ev.endDate}</td>
                                <td style={tdStyle}>{ev.troughDate}</td>
                                <td style={{ ...tdStyle, color: COLORS.negative }}>{fmtPct(ev.drawdownPct, 3)}</td>
                                <td style={{ ...tdStyle, color: COLORS.negative, fontWeight: 600 }}>
                                  {ev.jtLossPct <= -1 ? '−100%' : fmtPct(ev.jtLossPct)}
                                </td>
                                <td style={tdStyle}>
                                  <span style={{
                                    padding: '2px 6px', borderRadius: 2, fontSize: 9,
                                    background: ev.jtLossCause === 'Recovery duration exceeded' ? COLORS.cautionBg : COLORS.negativeBg,
                                    color: ev.jtLossCause === 'Recovery duration exceeded' ? COLORS.caution : COLORS.negative,
                                    border: `1px solid ${ev.jtLossCause === 'Recovery duration exceeded' ? COLORS.cautionBorder : COLORS.negativeBorder}`,
                                  }}>
                                    {ev.jtLossCause}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ marginTop: 6, padding: '6px 8px', background: COLORS.surfaceAlt, borderRadius: 3, fontSize: 10, fontFamily: "'Fragment Mono', monospace", color: COLORS.textSecondary, lineHeight: 1.5 }}>
                          Episodes: {drawdownEpisodes.length} | Finalized: {jtLossEvents.length} | Recovered: {drawdownEpisodes.filter(e => e.recovered).length}
                        </div>
                      </div>
                    );
                    })()}
                  </div>
                )}
              </Card>
            ) : (
              <Card title="Backtest Summary">
                <div style={{ padding: 12, color: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace", fontSize: 11 }}>
                  No backtest data yet.
                </div>
              </Card>
            )}
            <Card title="YDM Curve Shape">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={curveData} margin={chartMargin}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis
                    dataKey="u"
                    tickFormatter={(v) => fmtPct(v, 0)}
                    tick={tickStyle}
                  />
                  <YAxis
                    tickFormatter={(v) => fmtPct(v, 1)}
                    tick={tickStyle}
                  />
                  <Tooltip content={<ChartTooltip labelFormatter={(d) => `U = ${(d.u * 100).toFixed(1)}%`} />} />
                  <Line type="monotone" dataKey="y" stroke={COLORS.accent} strokeWidth={1.5} dot={false} name="Yield Share" />
                  <ReferenceDot x={currentU} y={currentY} r={5} fill={COLORS.accent} stroke={COLORS.surface} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Backtest charts */}
          {!navData ? (
            <Card>
              <div style={{ padding: 24, textAlign: 'center', color: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace", fontSize: 12 }}>
                No NAV data available for "{market.underlying.dataKey || 'this market'}".
                Backtest charts require historical NAV or APY data.
              </div>
            </Card>
          ) : (
            <>
              {/* Cumulative Returns */}
              <Card title="Backtested Cumulative Returns">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={cumReturnData} margin={chartMargin}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                    <XAxis
                      dataKey="ts"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      ticks={makeTimeTicks(cumReturnData)}
                      tick={tickStyle}
                      tickFormatter={fmtDateTick}
                    />
                    <YAxis
                      tickFormatter={(v) => fmtPct(v, 1)}
                      tick={tickStyle}
                    />
                    <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} />} />
                    <Line type="monotone" dataKey="underlying" stroke={COLORS.textTertiary} strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Underlying" />
                    <Line type="monotone" dataKey="st" stroke={COLORS.accent} strokeWidth={1.5} dot={false} name="ST Return" />
                    <Line type="monotone" dataKey="jt" stroke={COLORS.positive} strokeWidth={1.5} dot={false} name="JT Return" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* Drawdown Analysis */}
              {drawdownChartData.length > 0 && (
                <Card title="Drawdown Analysis">
                  <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                    {market.params.minCoverage > 0 && (
                      <label style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, color: COLORS.negative, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={showCoverageLine} onChange={(e) => setShowCoverageLine(e.target.checked)} style={{ accentColor: COLORS.negative }} />
                        COVERAGE EXHAUSTION
                      </label>
                    )}
                    {(market.liquidation.finalizedLossThreshold || 0) > 0 && (
                      <label style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, color: COLORS.caution, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={showLiquidationLine} onChange={(e) => setShowLiquidationLine(e.target.checked)} style={{ accentColor: COLORS.caution }} />
                        LIQUIDATION THRESHOLD
                      </label>
                    )}
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={drawdownChartData} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                      <XAxis
                        dataKey="ts"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        ticks={makeTimeTicks(drawdownChartData)}
                        tick={tickStyle}
                        tickFormatter={fmtDateTick}
                      />
                      <YAxis
                        tickFormatter={(v) => fmtPct(v, 1)}
                        tick={tickStyle}
                        domain={[
                          Math.min(
                            showCoverageLine && market.params.minCoverage > 0 ? coverageExhaustionLevel * 1.1 : 0,
                            showLiquidationLine && market.liquidation.finalizedLossThreshold > 0 ? liquidationThreshold * 1.1 : 0,
                            Math.min(...drawdownChartData.map(d => d.drawdown)) * 1.1 || -0.01,
                          ),
                          0
                        ]}
                      />
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div style={{ background: COLORS.ink, color: COLORS.bg, padding: '8px 12px', borderRadius: 2, fontFamily: "'Fragment Mono', monospace", fontSize: 11, lineHeight: 1.6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                            <div style={{ marginBottom: 2 }}>{d.date}</div>
                            <div>Drawdown: {fmtPct(d.drawdown, 2)}</div>
                            {showCoverageLine && market.params.minCoverage > 0 && (
                              <div style={{ color: COLORS.negativeBorder }}>Coverage exhaustion: {fmtPct(coverageExhaustionLevel, 2)}</div>
                            )}
                            {showLiquidationLine && market.liquidation.finalizedLossThreshold > 0 && (
                              <div style={{ color: COLORS.cautionBorder }}>Liquidation threshold: {fmtPct(liquidationThreshold, 2)}</div>
                            )}
                          </div>
                        );
                      }} />
                      {/* Episode boxes — green=recovered, red=finalized loss, yellow=ongoing */}
                      {drawdownEpisodes.map((ep, i) => {
                        const isSelected = selectedEpisode === ep;
                        const { color: epColor, border: epBorder } = episodeColors(ep);
                        return (
                          <ReferenceArea
                            key={`ep-${i}`}
                            x1={toMs(ep.startDate)}
                            x2={toMs(ep.endDate)}
                            y1={ep.startDrawdown != null ? ep.startDrawdown : 0}
                            y2={ep.troughDrawdown != null ? ep.troughDrawdown : ep.drawdownPct}
                            fill={epColor}
                            fillOpacity={isSelected ? 0.25 : ep.recovered ? 0.08 : 0.12}
                            stroke={epBorder}
                            strokeDasharray={isSelected ? '0' : '3 3'}
                            strokeWidth={isSelected ? 2 : 1}
                            onClick={() => setSelectedEpisode(isSelected ? null : ep)}
                            style={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                      {/* Coverage exhaustion line */}
                      {showCoverageLine && market.params.minCoverage > 0 && (
                        <ReferenceLine
                          y={coverageExhaustionLevel}
                          stroke={COLORS.negative}
                          strokeDasharray="6 4"
                          label={{ value: 'Coverage exhausted', position: 'insideBottomLeft', fontSize: 10, fill: COLORS.negative, fontFamily: "'Fragment Mono', monospace" }}
                        />
                      )}
                      {/* Liquidation threshold line */}
                      {showLiquidationLine && market.liquidation.finalizedLossThreshold > 0 && (
                        <ReferenceLine
                          y={liquidationThreshold}
                          stroke={COLORS.caution}
                          strokeDasharray="6 4"
                          label={{ value: 'Liquidation threshold', position: 'insideTopLeft', fontSize: 10, fill: COLORS.caution, fontFamily: "'Fragment Mono', monospace" }}
                        />
                      )}
                      {/* Drawdown line */}
                      <Line type="monotone" dataKey="drawdown" stroke={COLORS.ink} strokeWidth={1.5} dot={false} name="Drawdown" />
                    </LineChart>
                  </ResponsiveContainer>
                  {drawdownEpisodes.length > 0 && (
                    <div style={{ marginTop: 12, fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>
                      <div>
                        {drawdownEpisodes.length} drawdown episode{drawdownEpisodes.length !== 1 ? 's' : ''} detected
                        {jtLossEvents.length > 0 && (
                          <span style={{ color: COLORS.negative, marginLeft: 8 }}>
                            — {jtLossEvents.length} JT loss event{jtLossEvents.length !== 1 ? 's' : ''} finalized
                          </span>
                        )}
                      </div>
                      <div style={{ marginTop: 4, fontStyle: 'italic', color: COLORS.textTertiary }}>
                        Click an episode box to inspect. Chart assumes no juniors nor seniors have entered or left in between drawdown episodes.
                      </div>
                    </div>
                  )}
                  {selectedEpisode && (
                    <EpisodeDetail episode={selectedEpisode} onClose={() => setSelectedEpisode(null)} />
                  )}
                </Card>
              )}

              {/* Daily Return + Trailing 7d APY (synced cursor) */}
              <Card title="Daily Return & Trailing 7-Day APY">
                <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, color: COLORS.textSecondary, marginBottom: 2 }}>Daily Return</div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={dailyReturnData} syncId="apySync" margin={{ ...chartMargin, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                    <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']} hide />
                    <YAxis
                      tickFormatter={(v) => fmtPct(v, 2)}
                      tick={tickStyle}
                    />
                    <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} />} />
                    {(backtestResult?.fixedTermPeriods || []).map((p, i) => (
                      <ReferenceArea
                        key={`ftd-${i}`}
                        x1={toMs(p.start)}
                        x2={toMs(p.end)}
                        fill={COLORS.caution}
                        fillOpacity={0.10}
                        stroke={COLORS.cautionBorder}
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        label={i === 0 ? { value: 'Fixed-Term', position: 'insideTop', fontSize: 9, fill: COLORS.caution, fontFamily: "'Fragment Mono', monospace" } : undefined}
                      />
                    ))}
                    <Line type="monotone" dataKey="underlying" stroke={COLORS.textTertiary} strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Underlying" />
                    <Line type="monotone" dataKey="st" stroke={COLORS.accent} strokeWidth={1.5} dot={false} name="ST" />
                    <Line type="monotone" dataKey="jt" stroke={COLORS.positive} strokeWidth={1.5} dot={false} name="JT" />
                  </LineChart>
                </ResponsiveContainer>
                <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, color: COLORS.textSecondary, marginTop: 8, marginBottom: 2 }}>Trailing 7-Day APY</div>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trailingApyData} syncId="apySync" margin={chartMargin}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                    <XAxis
                      dataKey="ts"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      ticks={makeTimeTicks(trailingApyData)}
                      tick={tickStyle}
                      tickFormatter={fmtDateTick}
                    />
                    <YAxis
                      tickFormatter={(v) => fmtPct(v, 1)}
                      tick={tickStyle}
                    />
                    <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} />} />
                    {(backtestResult?.fixedTermPeriods || []).map((p, i) => (
                      <ReferenceArea
                        key={`ft-${i}`}
                        x1={toMs(p.start)}
                        x2={toMs(p.end)}
                        fill={COLORS.caution}
                        fillOpacity={0.10}
                        stroke={COLORS.cautionBorder}
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        label={i === 0 ? { value: 'Fixed-Term', position: 'insideTop', fontSize: 9, fill: COLORS.caution, fontFamily: "'Fragment Mono', monospace" } : undefined}
                      />
                    ))}
                    {(backtestResult?.fixedTermInfluenceZones || []).map((z, i) => (
                      <ReferenceArea
                        key={`fti-${i}`}
                        x1={toMs(z.start)}
                        x2={toMs(z.end)}
                        fill={COLORS.accentAlt}
                        fillOpacity={0.08}
                        stroke={COLORS.accentAlt}
                        strokeDasharray="6 4"
                        strokeWidth={0.5}
                        label={i === 0 ? { value: 'FT influence', position: 'insideTop', fontSize: 9, fill: COLORS.accentAlt, fontFamily: "'Fragment Mono', monospace" } : undefined}
                      />
                    ))}
                    <Line type="monotone" dataKey="underlying" stroke={COLORS.textTertiary} strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Underlying APY" />
                    <Line type="monotone" dataKey="st" stroke={COLORS.accent} strokeWidth={1.5} dot={false} name="ST APY" />
                    <Line type="monotone" dataKey="jt" stroke={COLORS.positive} strokeWidth={1.5} dot={false} name="JT APY" />
                  </LineChart>
                </ResponsiveContainer>
                <div style={{
                  fontFamily: "'Fragment Mono', monospace",
                  fontSize: 10,
                  color: COLORS.textTertiary,
                  lineHeight: 1.5,
                  padding: '6px 0 0',
                }}>
                  During Fixed-Term, all pool gains are allocated to Junior IL recovery (up to full recovery; excess distributes via YDM).
                  The trailing window is not reset at Fixed-Term boundaries — shaded 'FT influence' zones mark where the 7-day lookback
                  includes Fixed-Term activity, which is not representative of steady-state yield.
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
